import { useState, useRef, useEffect } from 'react'
import { getGuideline } from '../../data/discordGuidelines'
import styles from './GuidelineTooltip.module.css'

interface GuidelineTooltipProps {
  guidelineNumber: string
  language: 'cs' | 'en'
  children: React.ReactNode
}

function GuidelineTooltip({ guidelineNumber, language, children }: GuidelineTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const guideline = getGuideline(guidelineNumber)
  const title = guideline?.title[language]
  const content = guideline?.content[language]

  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return
    const trigger = triggerRef.current.getBoundingClientRect()
    const tooltip = tooltipRef.current.getBoundingClientRect()
    const top =
      trigger.bottom + tooltip.height + 16 <= window.innerHeight
        ? trigger.bottom + 8
        : Math.max(16, trigger.top - tooltip.height - 8)
    const left = Math.max(
      tooltip.width / 2 + 16,
      Math.min(trigger.left + trigger.width / 2, window.innerWidth - tooltip.width / 2 - 16),
    )
    setPosition({ top, left })
  }, [isVisible])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const tooltipHeight = 200 // Approximate height
      const tooltipWidth = Math.min(400, window.innerWidth - 32)

      // Check if tooltip should appear above or below
      const spaceBelow = window.innerHeight - rect.bottom - 20
      const shouldShowAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight

      // Calculate position relative to viewport
      let top = shouldShowAbove ? rect.top - tooltipHeight - 10 : rect.bottom + 10
      let left = rect.left + rect.width / 2

      // Check if tooltip would go off screen horizontally
      if (left - tooltipWidth / 2 < 10) {
        left = tooltipWidth / 2 + 10
      } else if (left + tooltipWidth / 2 > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth / 2 - 10
      }

      setPosition({ top, left })
      setIsVisible(true)
    }
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 100) // Small delay before hiding
  }

  const handleTooltipMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleTooltipMouseLeave = () => {
    handleMouseLeave()
  }

  if (!content) return <>{children}</>

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setIsVisible(false)
        }}
        className={styles.trigger}
      >
        {children}
      </span>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          role="note"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          onFocus={handleTooltipMouseEnter}
          onBlur={handleTooltipMouseLeave}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              triggerRef.current?.querySelector('a')?.focus()
              setIsVisible(false)
            }
          }}
        >
          <div className={styles.tooltipContent}>
            <div className={styles.tooltipHeader}>
              {language === 'cs' ? `Pokyn #${guidelineNumber}` : `Guideline #${guidelineNumber}`}
            </div>
            <div className={styles.tooltipBody}>
              <strong>
                {guideline?.number}. {title}
              </strong>
              <div className={styles.tooltipText}>{content}</div>
            </div>
            <div className={styles.tooltipFooter}>
              <a
                href={`/discord/guidelines#guideline-${guidelineNumber}`}
                className={styles.fullLink}
              >
                {language === 'cs' ? 'Zobrazit v pokynech →' : 'View in guidelines →'}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GuidelineTooltip
